import type { IClaims, IClaimsData, IFeatureFlag, IFeatureFlags } from '../../types';

/**
 * Checks if a feature flag is active.
 * @param featureFlag - The feature flag to check
 * @returns True if the feature is active, false otherwise
 */
export const isFeatureActive = (featureFlag: IFeatureFlag) => featureFlag.active;

/**
 * Checks if a feature flag is visible.
 * @param featureFlag - The feature flag to check
 * @returns True if the feature is visible, false otherwise
 */
export const isFeatureVisible = (featureFlag: IFeatureFlag) => featureFlag.visible;

/**
 * Checks if a feature flag is editable.
 * @param featureFlag - The feature flag to check
 * @returns True if the feature is editable, false otherwise
 */
export const isFeatureEditable = (featureFlag: IFeatureFlag) => featureFlag.editable;

/**
 * Checks if a feature flag is both active and visible.
 * @param featureFlag - The feature flag to check
 * @returns True if the feature is both visible and active, false otherwise
 */
export const isFeatureActiveAndVisible = (featureFlag: IFeatureFlag) =>
  featureFlag.visible && featureFlag.active;

/**
 * Retrieves and merges feature flags from API claims with existing state.
 * Iterates through existing feature flags and updates them with data from the API response.
 * If a feature flag is found in the API claims, it marks it as active and merges the properties.
 *
 * @param action - Action object containing claims from the API with feature flags
 * @param state - Current claims data state containing existing feature flags
 * @returns Updated feature flags object with merged data from API and state
 */
export function getFeatureFlags(action: { claims: IClaims }, state: IClaimsData): IFeatureFlags {
  // Create a shallow copy of the existing feature flags state
  const featureFlagsState: IFeatureFlags = { ...state.featureFlags };

  // Iterate through each feature flag in the current state
  Object.keys(featureFlagsState).forEach((featureFlagName: string) => {
    // Find the corresponding feature flag from the API claims
    const featureFlagFromApi = action?.claims?.featureFlags.find(
      (flag) => flag.name === featureFlagName
    );

    // If the feature flag exists in the API response, update it in the state
    if (featureFlagFromApi) {
      featureFlagsState[featureFlagName as keyof IFeatureFlags] = {
        ...featureFlagFromApi,
        active: true, // Mark the feature flag as active
      };
    }
  });

  return featureFlagsState;
}
